import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, fetchAudio } from './api';

export type VozGenero = 'M' | 'F';

export interface Speech {
  falando: boolean;
  carregando: boolean;
  erro: string | null;
  falar: (input: {
    texto: string;
    genero: VozGenero;
    cardId?: number;
    parte?: 'front' | 'back';
  }) => Promise<void>;
  parar: () => void;
}

/**
 * Leitura em voz alta.
 *
 * O caminho principal é o `/api/tts`, que devolve MP3 das vozes da Microsoft e
 * guarda o resultado em disco — o mesmo áudio nunca é sintetizado duas vezes.
 * O recurso ao `speechSynthesis` do navegador existe porque o serviço externo
 * pode estar indisponível, e ficar sem áudio nenhum é pior que uma voz menos
 * natural.
 */
export function useSpeech(): Speech {
  const [falando, setFalando] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  const limpar = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
  }, []);

  const parar = useCallback(() => {
    limpar();
    setFalando(false);
    setCarregando(false);
  }, [limpar]);

  useEffect(() => limpar, [limpar]);

  const falar = useCallback<Speech['falar']>(
    async ({ texto, genero, cardId, parte }) => {
      const limpo = texto.replace(/\s+/g, ' ').trim();
      if (!limpo) return;

      limpar();
      setErro(null);
      setCarregando(true);

      try {
        const blob = await fetchAudio({
          text: limpo,
          gender: genero,
          ...(cardId === undefined ? {} : { cardId }),
          ...(parte === undefined ? {} : { part: parte }),
        });

        const url = URL.createObjectURL(blob);
        urlRef.current = url;

        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => {
          setFalando(false);
          limpar();
        };
        audio.onerror = () => {
          setFalando(false);
          limpar();
        };

        setCarregando(false);
        setFalando(true);
        await audio.play();
      } catch (e) {
        setCarregando(false);
        if (e instanceof ApiError && (e.isUnauthorized || e.status === 403)) {
          setErro('Sua sessão expirou. Entre novamente para ouvir os cards.');
          setFalando(false);
          return;
        }
        if (!falarNoNavegador(limpo, genero, () => setFalando(false))) {
          setErro('Não consegui gerar o áudio agora.');
          setFalando(false);
          return;
        }
        setFalando(true);
      }
    },
    [limpar],
  );

  return { falando, carregando, erro, falar, parar };
}

function falarNoNavegador(
  texto: string,
  genero: VozGenero,
  aoTerminar: () => void,
): boolean {
  if (typeof speechSynthesis === 'undefined') return false;

  const vozes = speechSynthesis.getVoices().filter((v) => v.lang.startsWith('pt'));
  if (vozes.length === 0) return false;

  const utterance = new SpeechSynthesisUtterance(texto);
  utterance.lang = 'pt-BR';
  utterance.rate = 1;

  const preferida =
    vozes.find((v) =>
      genero === 'F'
        ? /female|francisca|maria|luciana/i.test(v.name)
        : /male|antonio|daniel|felipe/i.test(v.name),
    ) ?? vozes[0];
  if (preferida) utterance.voice = preferida;

  utterance.onend = aoTerminar;
  utterance.onerror = aoTerminar;

  speechSynthesis.speak(utterance);
  return true;
}
